/**
 * Overview de andamento por empresa (Super Admin).
 * Preferir GET /admin/companies/{id}/overview via superAdmin.getCompanyOverview.
 * Funções abaixo servem de fallback local / export.
 */

import { reportsMock } from './dashboardMock';
import { MONTH_LABELS, formatCurrencyBRL, formatNumberBR } from './formatters';

function hasText(value) {
    return value != null && String(value).trim() !== '';
}

function toFlag(value) {
    return value === true || value === 1 || value === '1';
}

function extractWebsite(source) {
    if (!source || typeof source !== 'object') return null;
    if (source.website) return source.website;
    if (Array.isArray(source.listWebsite) && source.listWebsite[0]) return source.listWebsite[0];
    if (Array.isArray(source.websites) && source.websites[0]) return source.websites[0];
    if (source.url || source.title || source.logo || source.logoFinal) return source;
    return null;
}

function seededRandom(seed) {
    let t = seed + 0x6d2b79f5;
    return () => {
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/**
 * KPIs locais por companyId (fallback quando overview da API falha).
 */
export function getCompanyOverviewMetrics(companyId) {
    const id = Number(companyId);
    const fromReports = Array.isArray(reportsMock.clients)
        ? reportsMock.clients.find((row) => Number(row.id) === id)
        : null;

    if (fromReports) {
        return {
            isMock: true,
            students: Number(fromReports.students) || 0,
            courses: Number(fromReports.courses) || 0,
            sales: Number(fromReports.sales) || 0,
            revenue: Number(fromReports.revenue) || 0,
        };
    }

    const seed = Number.isFinite(id) && id > 0 ? id : 1;
    return {
        isMock: true,
        students: (seed * 37) % 800,
        courses: (seed * 11) % 40,
        sales: (seed * 19) % 120,
        revenue: ((seed * 1300) % 85000),
    };
}

/**
 * Séries gráficas mock derivadas dos KPIs (últimos 6 meses).
 */
export function getCompanyOverviewCharts(metrics = {}, companyId = 1) {
    const id = Number(companyId) || 1;
    const rand = seededRandom(id * 97);
    const students = Number(metrics.students) || 0;
    const courses = Number(metrics.courses) || 0;
    const sales = Number(metrics.sales) || 0;
    const revenue = Number(metrics.revenue) || 0;

    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i -= 1) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push({
            key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
            label: MONTH_LABELS[d.getMonth()],
            month: d.getMonth() + 1,
            year: d.getFullYear(),
        });
    }

    const revenueTrend = months.map((m, index) => {
        const factor = 0.55 + (index / 5) * 0.35 + rand() * 0.12;
        return {
            ...m,
            value: Math.max(0, Math.round((revenue / 6) * factor)),
        };
    });

    const salesTrend = months.map((m, index) => {
        const factor = 0.5 + (index / 5) * 0.4 + rand() * 0.15;
        return {
            ...m,
            value: Math.max(0, Math.round((sales / 6) * factor)),
        };
    });

    const studentsTrend = months.map((m, index) => {
        const factor = 0.7 + (index / 5) * 0.25 + rand() * 0.08;
        return {
            ...m,
            value: Math.max(0, Math.round((students / 1.15) * factor)),
        };
    });

    return {
        isMock: true,
        months: months.map((m) => m.label),
        revenueTrend,
        salesTrend,
        studentsTrend,
        composition: [
            { label: 'Alunos', value: students, color: '#250891' },
            { label: 'Cursos', value: courses, color: '#7C5CFC' },
            { label: 'Vendas', value: sales, color: '#1AC7D0' },
        ],
    };
}

/**
 * Checklist de configuração + progresso %.
 * @param {object} company
 * @param {object|null} website
 * @param {object} metrics
 * @param {object|null} videoteca status (folderId / hasToken; nunca inclui o token)
 */
export function buildCompanyChecklist(company, website = null, metrics = {}, videoteca = null) {
    const c = company || {};
    const w = website || extractWebsite(c) || {};
    const coursesCount = Number(metrics.courses) || 0;
    const salesCount = Number(metrics.sales) || 0;
    const isWeeduc = Number(c.typeSystem) === 1;
    const videoStatus = normalizeVideotecaStatus(videoteca, c);

    const contents = Array.isArray(w.contents) ? w.contents : [];
    const hasActiveContent = contents.some((item) => toFlag(item?.status));

    const items = [
        {
            id: 'cadastro',
            label: 'Cadastro básico',
            hint: 'Razão social, e-mail, CNPJ e telefone',
            done: hasText(c.name) && hasText(c.email) && hasText(c.idFiscal) && hasText(c.phone),
        },
        {
            id: 'endereco',
            label: 'Endereço',
            hint: 'CEP e cidade preenchidos',
            done: hasText(c.postcode) && hasText(c.city),
        },
        {
            id: 'venda-aprovada',
            label: 'Venda aprovada',
            hint: 'Status de carrinho/aprovação liberado',
            done: toFlag(c.cartStatus),
        },
        {
            id: 'site-ativo',
            label: 'Site ativo',
            hint: 'Site público habilitado',
            done: toFlag(w.status),
        },
        {
            id: 'dominio',
            label: 'Domínio configurado',
            hint: 'URL principal do site',
            done: hasText(w.url),
        },
        {
            id: 'identidade',
            label: 'Identidade visual',
            hint: 'Logo ou favicon',
            done: hasText(w.logo) || hasText(w.logoFinal) || hasText(w.favicon) || hasText(w.faviconFinal),
        },
        {
            id: 'seo',
            label: 'SEO básico',
            hint: 'Título e descrição do site',
            done: hasText(w.title || w.nome) && hasText(w.description),
        },
        {
            id: 'conteudo',
            label: 'Conteúdo da home',
            hint: 'Ao menos um bloco ativo',
            done: hasActiveContent,
        },
        buildVideotecaChecklistItem(videoStatus),
        {
            id: 'cursos',
            label: 'Cursos no catálogo',
            hint: 'Já possui cursos publicados',
            done: coursesCount > 0,
        },
        {
            id: 'vendas',
            label: 'Teve vendas',
            hint: 'Vendas registradas ou venda aprovada',
            done: salesCount > 0 || toFlag(c.cartStatus),
        },
    ];

    if (isWeeduc) {
        const portalIndex = items.findIndex((item) => item.id === 'cursos');
        items.splice(portalIndex, 0, {
            id: 'portal',
            label: 'Portal configurado',
            hint: 'Layout do portal ou módulos liberados',
            done:
                w.typeLayoutPortal != null
                || toFlag(w.showCertificate)
                || toFlag(w.showInquiry)
                || toFlag(w.showForum)
                || hasText(w.portalBannerPath)
                || hasText(w.portalBannerPathFinal),
        });
    }

    return finalizeChecklist(items);
}

/**
 * Normaliza status da Videoteca (GET /company/videoteca ou admin equivalente).
 * Não espera nem propaga o token em si.
 */
export function normalizeVideotecaStatus(videoteca = null, company = null) {
    const v = videoteca && typeof videoteca === 'object' ? videoteca : {};
    const c = company && typeof company === 'object' ? company : {};
    const folderId =
        v.folderId
        || v.idFolder
        || v.idFolderVideoteca
        || c.idFolderVideoteca
        || null;
    const hasToken = toFlag(
        v.hasToken
        ?? v.hasAccount
        ?? v.tokenConfigured
        ?? v.hasVideotecaToken
        ?? v.accountLinked,
    );
    const skipEnabled = toFlag(v.skipEnabled);
    const ok = v.ok !== false;
    const configured = Boolean(folderId) && hasToken && ok && !skipEnabled;

    return {
        folderId: folderId ? String(folderId) : null,
        hasToken,
        skipEnabled,
        ok,
        configured,
        message: v.message || null,
    };
}

export function buildVideotecaChecklistItem(videoteca = null) {
    const status = normalizeVideotecaStatus(videoteca);
    let hint = 'Pasta e token da conta configurados';
    if (status.skipEnabled) {
        hint = 'Criação ignorada neste ambiente';
    } else if (!status.hasToken && !status.folderId) {
        hint = 'Falta token da conta e pasta na Videoteca';
    } else if (!status.hasToken) {
        hint = 'Pasta criada — falta token da conta';
    } else if (!status.folderId) {
        hint = 'Token vinculado — falta criar a pasta';
    }

    return {
        id: 'videoteca',
        label: 'Videoteca integrada',
        hint,
        done: status.configured,
    };
}

/**
 * Garante o item Videoteca no checklist (API ou fallback local).
 */
export function mergeVideotecaIntoChecklist(checklist = {}, videoteca = null, company = null) {
    const status = normalizeVideotecaStatus(videoteca, company);
    const item = buildVideotecaChecklistItem(status);
    const currentItems = Array.isArray(checklist?.items) ? [...checklist.items] : [];
    const withoutVideo = currentItems.filter((entry) => entry?.id !== 'videoteca');
    const cursosIndex = withoutVideo.findIndex((entry) => entry?.id === 'cursos');
    if (cursosIndex >= 0) {
        withoutVideo.splice(cursosIndex, 0, item);
    } else {
        withoutVideo.push(item);
    }
    return finalizeChecklist(withoutVideo);
}

function finalizeChecklist(items) {
    const doneCount = items.filter((item) => item.done).length;
    const total = items.length;
    const percent = total ? Math.round((doneCount / total) * 100) : 0;
    return {
        items,
        doneCount,
        total,
        percent,
    };
}

export function buildCompanyOverview(company, website = null, videoteca = null) {
    const metrics = getCompanyOverviewMetrics(company?.id);
    const checklist = buildCompanyChecklist(company, website, metrics, videoteca);
    const charts = getCompanyOverviewCharts(metrics, company?.id);
    return {
        metrics,
        checklist,
        charts,
        isMock: Boolean(metrics.isMock),
        companyName: company?.name || company?.tradeName || `Empresa ${company?.id || ''}`,
        companyId: company?.id || null,
    };
}

function escapeXml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function sheetRowsXml(rows) {
    return rows.map((row) => {
        const cells = row.map((cell) => {
            const isNumber = typeof cell === 'number' && Number.isFinite(cell);
            if (isNumber) {
                return `<Cell><Data ss:Type="Number">${cell}</Data></Cell>`;
            }
            return `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`;
        }).join('');
        return `<Row>${cells}</Row>`;
    }).join('');
}

/**
 * Gera e baixa .xls (SpreadsheetML) com KPIs, checklist e tendências.
 * Abre nativamente no Excel sem dependência extra.
 */
export function exportCompanyOverviewXls(overview = {}, companyMeta = {}) {
    const metrics = overview.metrics || {};
    const checklist = overview.checklist || { items: [] };
    const charts = overview.charts || {};
    const name = companyMeta.name || overview.companyName || 'Empresa';
    const id = companyMeta.id || overview.companyId || '';

    const summaryRows = [
        ['Campo', 'Valor'],
        ['Empresa', name],
        ['ID', id],
        ['Alunos', Number(metrics.students) || 0],
        ['Cursos', Number(metrics.courses) || 0],
        ['Vendas', Number(metrics.sales) || 0],
        ['Faturamento (R$)', Number(metrics.revenue) || 0],
        ['Configuração (%)', Number(checklist.percent) || 0],
        ['Itens concluídos', `${checklist.doneCount || 0} de ${checklist.total || 0}`],
        ['Fonte', metrics.isMock ? 'Preview / mock' : 'API'],
    ];

    const checklistRows = [
        ['Item', 'Status', 'Detalhe'],
        ...(checklist.items || []).map((item) => [
            item.label,
            item.done ? 'Concluído' : 'Pendente',
            item.hint || '',
        ]),
    ];

    const trendRows = [
        ['Mês', 'Receita (R$)', 'Vendas', 'Alunos'],
        ...(charts.revenueTrend || []).map((row, index) => [
            `${row.label}/${String(row.year).slice(2)}`,
            row.value,
            charts.salesTrend?.[index]?.value ?? 0,
            charts.studentsTrend?.[index]?.value ?? 0,
        ]),
    ];

    const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Worksheet ss:Name="Resumo">
  <Table>${sheetRowsXml(summaryRows)}</Table>
 </Worksheet>
 <Worksheet ss:Name="Checklist">
  <Table>${sheetRowsXml(checklistRows)}</Table>
 </Worksheet>
 <Worksheet ss:Name="Tendencias">
  <Table>${sheetRowsXml(trendRows)}</Table>
 </Worksheet>
</Workbook>`;

    const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const slug = String(name)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w]+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase()
        .slice(0, 40) || 'empresa';
    const fileName = `andamento-${slug}-${new Date().toISOString().slice(0, 10)}.xls`;
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);

    return {
        fileName,
        summaryLabel: `${formatNumberBR(metrics.students || 0)} alunos · ${formatCurrencyBRL(metrics.revenue || 0)}`,
    };
}

export { extractWebsite };
